/**
 * „Na rynku od N dni" — reguła osobno od komponentu, bo ma trzy warunki i każdy z nich
 * bierze się z pomiaru, nie z gustu.
 *
 * SKĄD DATA. `market_since` z warstwy serwującej: najstarsza publikacja w klastrze, liczona
 * od OSTATNIEGO ponownego wystawienia (przerwa > 30 dni). NIE `first_seen_at` — tamta mówi,
 * odkąd MY znamy wiersz, i 2 582 z 6 071 ofert ma tam dzień zbudowania warstwy serwującej
 * (pomiar z migracji `20260927132756`).
 *
 * PRÓG 14 DNI (decyzja Piotra 27.09). „Na rynku od 3 dni" nie jest informacją — każda świeża
 * oferta tak ma. Liczba zaczyna coś znaczyć dopiero wtedy, gdy zaczyna być sygnałem: że
 * mieszkanie nie schodzi, że jest pole do rozmowy o cenie. Mediana naszej podaży to 55 dni,
 * więc próg 14 odsiewa świeżynki, a nie „prawie wszystko".
 *
 * SUFIT KORPUSU. Najstarszy post w bazie jest z 29.05.2026, więc mieszkanie wystawione dwa
 * lata temu i tak pokaże najwyżej tyle, ile ma korpus. Dla ofert opartych o ten sufit
 * mówimy „ponad N dni" zamiast „od N dni" — bo to jest DOLNE OGRANICZENIE, a nie prawda o
 * rynku. Dotyczy 133 ofert (2,2%).
 */
export const POCZATEK_KORPUSU = '2026-05-29';
/** Poniżej tylu dni nie mówimy nic — patrz nagłówek. */
export const PROG_DNI_NA_RYNKU = 14;

export interface StanNaRynku {
  dni: number;
  /** Data opiera się o początek korpusu — mówimy „ponad", nie „od". */
  ponad: boolean;
}

const DZIEN = 24 * 60 * 60 * 1000;

/**
 * @param marketSince ISO z bazy (`flat.offers.market_since`); `null` dla ofert bez historii.
 * @param teraz punkt odniesienia — podawany jawnie, żeby test nie zależał od zegara.
 */
export function stanNaRynku(
  marketSince: string | null | undefined,
  teraz: Date = new Date(),
): StanNaRynku | null {
  if (!marketSince) return null;
  const od = new Date(marketSince);
  if (Number.isNaN(od.getTime())) return null;
  const dni = Math.floor((teraz.getTime() - od.getTime()) / DZIEN);
  if (dni < PROG_DNI_NA_RYNKU) return null;
  // Oferta „opiera się o sufit", gdy jej data jest z pierwszej doby korpusu: wcześniejszych
  // postów po prostu nie mamy, więc wszystko, co zaczyna się tam, może być starsze.
  const poczatek = new Date(`${POCZATEK_KORPUSU}T00:00:00Z`).getTime();
  const ponad = od.getTime() < poczatek + DZIEN;
  return { dni, ponad };
}

/**
 * KARTA W WYNIKACH (Piotr 7.10): sama data („4 dni temu”), bez „· na rynku od N dni” — z jednym
 * wyjątkiem: oferta WYSTAWIONA PONOWNIE, czyli `market_since` starsze od `posted_at` o ponad
 * tyle dni. Wtedy „4 dni temu” mówi o ostatnim poście, a mieszkanie wisi dłużej, i właśnie to
 * jest informacja (ok. 9% domyślnego SERP). Strona oferty pokazuje „na rynku od” zawsze, gdy
 * `stanNaRynku` coś zwraca.
 */
export const DNI_PONOWNEGO_WYSTAWIENIA = 7;

export function wystawionaPonownie(
  marketSince: string | null | undefined,
  postedAt: string | null | undefined,
): boolean {
  if (!marketSince || !postedAt) return false;
  const od = new Date(marketSince).getTime();
  const post = new Date(postedAt).getTime();
  if (Number.isNaN(od) || Number.isNaN(post)) return false;
  return post - od > DNI_PONOWNEGO_WYSTAWIENIA * DZIEN;
}
